import { Alert, Button, Col, Flex, Form, Row, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../components/Date&TimePickers/CustomDatePicker"
import InputNumber from "../../../components/Input/InputNumber"
import { TextInput } from "../../../components/Input/TextInput.tsx"
import SelectComponent from "../../../components/Select"
import { LOCAL_STATES } from "../../../constants/states"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { withoutOtherSpecialization } from "../constants/specialization.ts"
import { asArray } from "../helpers/list-values.ts"
import { fetchSubcontractorsFilterOptions } from "../redux/subcontractors.thunks"
import { type SubcontractorListItem } from "../types/types.ts"
import { DocumentUpload } from "./SubcontractorForm/DocumentUpload.tsx"
import {
  ACH_ACCOUNT_NUMBER_PATTERN,
  ACH_ROUTING_NUMBER_PATTERN,
  CSLB_LICENSE_NUMBER_PATTERN,
  EIN_PATTERN,
  SSN_PATTERN,
  TAX_ID_TYPE_OPTIONS,
  cslbStatusOptions,
  entityTypeOptions,
  normFile,
  paymentMethodOptions,
  validateBondAmount
} from "./SubcontractorForm/field-rules.ts"
import { type SubcontractorFormData } from "./SubcontractorForm/subcontractor-form-data.ts"
import {
  type SubcontractorFormValues,
  toSubcontractorFormData
} from "./SubcontractorForm/subcontractor-form-submit.ts"

const { Text } = Typography

export type SubcontractorEditSection = "identity" | "cslb" | "payment"

/**
 * Exactly the fields each section owns. A save sends these and nothing else,
 * so one section's write can never carry — or clear — another's values: the
 * API's `partial_update` leaves a key it was not sent alone.
 */
export const SECTION_FIELDS: Record<
  SubcontractorEditSection,
  (keyof SubcontractorFormData)[]
> = {
  identity: [
    "legal_business_name",
    "entity_type",
    "business_address",
    "business_city",
    "business_state",
    "business_zip_code",
    "ein",
    "ssn",
    "w9_document",
    "w9_validity_date"
  ],
  cslb: [
    "cslb_license_number",
    "cslb_classification",
    "cslb_status",
    "cslb_issue_date",
    "cslb_bond_amount",
    "cslb_bond_number",
    "cslb_qualifier_bond_amount",
    "cslb_qualifier_bond_number"
  ],
  payment: ["payment_method", "ach_routing_number", "ach_account_number"]
}

/**
 * What one section's Save puts on the wire.
 *
 * `toSubcontractorFormData` is reused unchanged so a cleared field still
 * travels as an explicit `null` rather than being dropped — but it marks every
 * clearable field absent from this form as cleared, so the section's own keys
 * are picked out afterwards. Picking last is what keeps a CSLB save from
 * wiping the business address.
 */
export const toSectionPayload = (
  section: SubcontractorEditSection,
  values: SubcontractorFormValues,
  initialValues: Partial<SubcontractorFormData>,
  taxIdType: "ein" | "ssn" = "ein",
  taxIdOnFile?: { ein_last4?: string | null; ssn_last4?: string | null }
): Partial<SubcontractorFormData> => {
  const data = toSubcontractorFormData(values, initialValues)

  if (section === "identity") {
    // Only the tax id the business says it holds is ever sent, and the one
    // being replaced is cleared only once a replacement has been entered: a
    // mis-click must not destroy a number nobody can read back to re-enter.
    const other = taxIdType === "ssn" ? "ein" : "ssn"
    const otherOnFile =
      other === "ein" ? taxIdOnFile?.ein_last4 : taxIdOnFile?.ssn_last4
    data[other] = data[taxIdType] && otherOnFile ? "" : undefined
  }

  return Object.fromEntries(
    SECTION_FIELDS[section]
      .filter((field) => data[field] !== undefined)
      .map((field) => [field, data[field]])
  )
}

interface Props {
  section: SubcontractorEditSection
  subcontractor: SubcontractorListItem
  /** Resolves false when the server refused, which keeps the card in edit
   *  mode with everything the user typed still in it. */
  onSave: (data: Partial<SubcontractorFormData>) => Promise<boolean>
  onCancel: () => void
}

/**
 * One section's fields, rendered in place of its card's values rather than in
 * a modal: the labels, the column spans and the order are the read view's, so
 * nothing moves when Edit is pressed.
 */
export const SubcontractorSectionForm = ({
  section,
  subcontractor,
  onSave,
  onCancel
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm<SubcontractorFormValues>()
  const [saving, setSaving] = useState(false)
  const fetching = useAppSelector((state) => state.subcontractors.saveFetching)
  const filterOptions = useAppSelector(
    (state) => state.subcontractors.filterOptions
  )

  const ENTITY_TYPE_OPTIONS = useMemo(() => entityTypeOptions(t), [t])
  const CSLB_STATUS_OPTIONS = useMemo(() => cslbStatusOptions(t), [t])
  const PAYMENT_METHOD_OPTIONS = useMemo(() => paymentMethodOptions(t), [t])

  // Which tax id the business holds is its own answer: what is already on
  // file wins, failing that the entity type is a first guess, not a rule.
  const [taxIdType, setTaxIdType] = useState<"ein" | "ssn">(() => {
    if (subcontractor.ssn_last4) return "ssn"
    if (subcontractor.ein_last4) return "ein"
    return subcontractor.entity_type === "sole_prop" ? "ssn" : "ein"
  })

  const bankDetailsOnFile = Boolean(subcontractor.ach_account_number_last4)
  const [replacingBankDetails, setReplacingBankDetails] = useState(false)
  const paymentMethod = Form.useWatch("payment_method", form)

  useEffect(() => {
    if (section === "cslb") dispatch(fetchSubcontractorsFilterOptions())
  }, [dispatch, section])

  /** The stored values the clear-detection compares against. */
  const storedValues = useMemo(() => {
    const record = subcontractor as unknown as Record<string, unknown>
    const picked: Record<string, unknown> = {}
    SECTION_FIELDS[section].forEach((field) => {
      if (field === "w9_document") return
      if (record[field] !== undefined) picked[field] = record[field]
    })
    return picked as Partial<SubcontractorFormData>
  }, [section, subcontractor])

  const initialValues = useMemo(() => {
    if (section === "identity") {
      return {
        legal_business_name: subcontractor.legal_business_name,
        entity_type: subcontractor.entity_type,
        business_address: subcontractor.business_address,
        business_city: subcontractor.business_city,
        business_state: subcontractor.business_state,
        business_zip_code: subcontractor.business_zip_code,
        // The API returns the existing W-9 as a URL string, not the fileList
        // shape the Upload expects; the file on record is not lost, just not
        // prefilled, and a new upload replaces it.
        w9_document: undefined,
        w9_validity_date: subcontractor.w9_validity_date
          ? dayjs(subcontractor.w9_validity_date)
          : undefined
      }
    }

    if (section === "cslb") {
      return {
        cslb_license_number: subcontractor.cslb_license_number,
        cslb_classification: asArray(subcontractor.cslb_classification),
        cslb_status: subcontractor.cslb_status,
        cslb_issue_date: subcontractor.cslb_issue_date
          ? dayjs(subcontractor.cslb_issue_date)
          : undefined,
        cslb_bond_amount: subcontractor.cslb_bond_amount,
        cslb_bond_number: subcontractor.cslb_bond_number,
        cslb_qualifier_bond_amount: subcontractor.cslb_qualifier_bond_amount,
        cslb_qualifier_bond_number: subcontractor.cslb_qualifier_bond_number
      }
    }

    return { payment_method: subcontractor.payment_method }
  }, [section, subcontractor])

  const [saveError, setSaveError] = useState<string | null>(null)

  const handleFinish = async (values: SubcontractorFormValues) => {
    if (saving) return

    setSaving(true)
    setSaveError(null)
    try {
      const saved = await onSave(
        toSectionPayload(section, values, storedValues, taxIdType, {
          ein_last4: subcontractor.ein_last4,
          ssn_last4: subcontractor.ssn_last4
        })
      )
      // The toast the thunk already raises carries the server's own words;
      // this keeps the refusal beside the fields that still hold the typing.
      if (!saved) setSaveError(t("subcontractorDetail.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  const identityFields = (
    <>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.legalBusinessName")}
            name={"legal_business_name"}
          >
            <TextInput
              placeholder={t("subcontractorForm.enterLegalBusinessName")}
              size={"large"}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.entityType")}
            name={"entity_type"}
          >
            <SelectComponent
              size={"large"}
              placeholder={t("subcontractorForm.selectEntityType")}
              options={ENTITY_TYPE_OPTIONS}
              optionFilterType={"form"}
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item<SubcontractorFormValues>
        label={t("subcontractorForm.businessAddress")}
        name={"business_address"}
      >
        <TextInput
          placeholder={t("subcontractorForm.streetAddress")}
          size={"large"}
        />
      </Form.Item>

      <Row gutter={12}>
        <Col xs={24} md={8}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.city")}
            name={"business_city"}
          >
            <TextInput
              placeholder={t("subcontractorForm.city")}
              size={"large"}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.state")}
            name={"business_state"}
          >
            <SelectComponent
              size={"large"}
              placeholder={t("subcontractorForm.selectState")}
              options={LOCAL_STATES}
              optionFilterType={"form"}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.zipCode")}
            name={"business_zip_code"}
          >
            <TextInput
              placeholder={t("subcontractorForm.zipCode")}
              size={"large"}
              disableAutoCapitalize
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item label={t("subcontractorForm.taxIdType")}>
            <SelectComponent
              size={"large"}
              value={taxIdType}
              onChange={(next) => setTaxIdType(next as "ein" | "ssn")}
              options={TAX_ID_TYPE_OPTIONS}
              optionFilterType={"form"}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          {taxIdType === "ssn" ? (
            <Form.Item<SubcontractorFormValues>
              label={t("subcontractorForm.ssn")}
              name={"ssn"}
              rules={[
                { pattern: SSN_PATTERN, message: t("subcontractorForm.errSsn") }
              ]}
            >
              <TextInput
                type={"password"}
                autoComplete={"off"}
                disableAutoCapitalize
                placeholder={
                  subcontractor.ssn_last4
                    ? `On file, ending ${subcontractor.ssn_last4}`
                    : "XXX-XX-XXXX"
                }
                size={"large"}
              />
            </Form.Item>
          ) : (
            <Form.Item<SubcontractorFormValues>
              label={t("subcontractorForm.ein")}
              name={"ein"}
              rules={[
                { pattern: EIN_PATTERN, message: t("subcontractorForm.errEin") }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                placeholder={
                  subcontractor.ein_last4
                    ? `On file, ending ${subcontractor.ein_last4}`
                    : "XX-XXXXXXX"
                }
                size={"large"}
              />
            </Form.Item>
          )}
        </Col>
      </Row>
      {/* Switching type replaces rather than deletes: the number already on
          file is only cleared once a new one is entered. */}
      {taxIdType === "ssn" && subcontractor.ein_last4 && (
        <Text type={"secondary"} className={"mb-3 block"}>
          {`An EIN ending ${subcontractor.ein_last4} is on file. Entering an SSN replaces it.`}
        </Text>
      )}
      {taxIdType === "ein" && subcontractor.ssn_last4 && (
        <Text type={"secondary"} className={"mb-3 block"}>
          {`An SSN ending ${subcontractor.ssn_last4} is on file. Entering an EIN replaces it.`}
        </Text>
      )}

      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.w9Document")}
            name={"w9_document"}
            valuePropName={"fileList"}
            getValueFromEvent={normFile}
          >
            <DocumentUpload
              buttonLabel={
                subcontractor.w9_document ? "Replace file" : "Select file"
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.w9ValidityDate")}
            name={"w9_validity_date"}
          >
            <CustomDatePicker
              className={"w-full"}
              size={"large"}
              format={"YYYY-MM-DD"}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )

  const cslbFields = (
    <>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.licenseNumber")}
            name={"cslb_license_number"}
            rules={[
              {
                pattern: CSLB_LICENSE_NUMBER_PATTERN,
                message: "Enter a valid CSLB license number (up to 8 digits)"
              }
            ]}
          >
            <TextInput
              placeholder={t("subcontractorForm.upToEightDigits")}
              size={"large"}
              disableAutoCapitalize
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.classification")}
            name={"cslb_classification"}
          >
            <SelectComponent
              size={"large"}
              mode={"multiple"}
              // No responsive collapse: in a half-width card it hides every
              // tag behind "+2", so the classifications wrap instead.
              placeholder={t("subcontractorForm.selectCslbClassification")}
              options={withoutOtherSpecialization(
                filterOptions?.["specialization"]
              )}
              optionFilterType={"form"}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.status")}
            name={"cslb_status"}
          >
            <SelectComponent
              size={"large"}
              placeholder={t("subcontractorForm.selectLicenseStatus")}
              options={CSLB_STATUS_OPTIONS}
              optionFilterType={"form"}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.issueDate")}
            name={"cslb_issue_date"}
          >
            <CustomDatePicker
              className={"w-full"}
              size={"large"}
              format={"YYYY-MM-DD"}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.contractorBondAmount")}
            name={"cslb_bond_amount"}
            rules={[{ validator: validateBondAmount }]}
          >
            <InputNumber
              size={"large"}
              prefix={"$"}
              style={{ width: "100%" }}
              placeholder={"25000"}
              min={0}
              precision={2}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.bondNumber")}
            name={"cslb_bond_number"}
          >
            <TextInput
              placeholder={t("subcontractorForm.enterBondNumber")}
              size={"large"}
              disableAutoCapitalize
            />
          </Form.Item>
        </Col>
      </Row>

      <Text type={"secondary"} className={"mb-2 block"}>
        {t("subcontractorForm.qualifierBondHint")}
      </Text>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.qualifierBondAmount")}
            name={"cslb_qualifier_bond_amount"}
            rules={[{ validator: validateBondAmount }]}
          >
            <InputNumber
              size={"large"}
              prefix={"$"}
              style={{ width: "100%" }}
              placeholder={"25000"}
              min={0}
              precision={2}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item<SubcontractorFormValues>
            label={t("subcontractorForm.qualifierBondNumber")}
            name={"cslb_qualifier_bond_number"}
          >
            <TextInput
              placeholder={t("subcontractorForm.enterBondNumber")}
              size={"large"}
              disableAutoCapitalize
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )

  const paymentFields = (
    <>
      <Form.Item<SubcontractorFormValues>
        label={t("subcontractorForm.paymentMethod")}
        name={"payment_method"}
      >
        <SelectComponent
          size={"large"}
          placeholder={t("subcontractorForm.selectPaymentMethod")}
          options={PAYMENT_METHOD_OPTIONS}
          optionFilterType={"form"}
          allowClear
        />
      </Form.Item>

      {paymentMethod === "ach" &&
        (bankDetailsOnFile && !replacingBankDetails ? (
          <Flex vertical gap={4} className={"mb-3"}>
            <Text type={"secondary"}>Bank details on file</Text>
            <Flex align={"center"} gap={12} wrap>
              <Text>
                {`Account ending ${subcontractor.ach_account_number_last4}`}
                {subcontractor.ach_routing_number_last4
                  ? `, routing ending ${subcontractor.ach_routing_number_last4}`
                  : ""}
              </Text>
              <Button onClick={() => setReplacingBankDetails(true)}>
                {t("subcontractorForm.replaceBankDetails")}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label={t("subcontractorForm.achRoutingNumber")}
                name={"ach_routing_number"}
                rules={[
                  {
                    pattern: ACH_ROUTING_NUMBER_PATTERN,
                    message: t("subcontractorForm.errRoutingNumber")
                  }
                ]}
              >
                <TextInput
                  disableAutoCapitalize
                  placeholder={
                    subcontractor.ach_routing_number_last4
                      ? `On file, ending ${subcontractor.ach_routing_number_last4}`
                      : "123456789"
                  }
                  size={"large"}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label={t("subcontractorForm.achAccountNumber")}
                name={"ach_account_number"}
                rules={[
                  {
                    pattern: ACH_ACCOUNT_NUMBER_PATTERN,
                    message: t("subcontractorForm.errAccountNumber")
                  }
                ]}
              >
                <TextInput
                  type={"password"}
                  autoComplete={"off"}
                  disableAutoCapitalize
                  placeholder={
                    subcontractor.ach_account_number_last4
                      ? `On file, ending ${subcontractor.ach_account_number_last4}`
                      : "Enter account number"
                  }
                  size={"large"}
                />
              </Form.Item>
            </Col>
          </Row>
        ))}
    </>
  )

  return (
    <Form<SubcontractorFormValues>
      form={form}
      layout={"vertical"}
      onFinish={handleFinish}
      validateTrigger={["onSubmit", "onChange"]}
      className={"with-default-error"}
      initialValues={initialValues}
    >
      {saveError && (
        <Alert type={"error"} showIcon message={saveError} className={"mb-4"} />
      )}

      {section === "identity" && identityFields}
      {section === "cslb" && cslbFields}
      {section === "payment" && paymentFields}

      <Form.Item noStyle>
        <Flex align={"center"} justify={"flex-end"} gap={12} className={"mt-2"}>
          <Button onClick={onCancel} disabled={saving}>
            {t("subcontractorForm.cancel")}
          </Button>
          <Button
            type={"primary"}
            htmlType={"submit"}
            loading={saving || fetching}
            disabled={saving}
          >
            {t("subcontractorForm.saveChanges")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}
